import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TokenService } from '../services/auth/token.service';
import { AuthService } from '../services/auth/auth.service';
import { environment } from '../../../environments/environment';
import { AuthStorageService } from '../services/auth/auth-storage.service';
import { SupportSessionService } from '../services/support/support-session.service';
import { CompanyTerminationService } from '../services/access/company-termination.service';
import { CompanyTerminationDetails } from '../models/smartclock/company-access.model';
import { companyTerminatedDetails } from '../../shared/utils/company-access.utils';
import { apiErrorOf } from '../../shared/utils/api-error.utils';

/**
 * 401 de una verificación de la contraseña con la sesión vigente: firmar o rechazar con la
 * contraseña como firma electrónica simple (`INVALID_PASSWORD`, labor-documents) o cambiar la
 * contraseña o el correo de acceso (`INVALID_CURRENT_PASSWORD`, auth). La persona escribió mal su
 * contraseña, pero su token sigue siendo válido: no se renueva ni se cierra la sesión, y el error
 * llega al componente, que muestra el mensaje del API.
 */
const CREDENTIAL_CHECK_FAILURES = ['INVALID_PASSWORD', 'INVALID_CURRENT_PASSWORD'];

function isCredentialCheckFailure(error: HttpErrorResponse): boolean {
  if (error.status !== 401) return false;
  const { type } = apiErrorOf(error);
  return !!type && CREDENTIAL_CHECK_FAILURES.includes(type);
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly tokenService = inject(TokenService);
  private readonly authService = inject(AuthService);
  private readonly authStorage = inject(AuthStorageService);
  private readonly router = inject(Router);
  // Resolución diferida (como en AccessDeniedInterceptor): SupportSessionService
  // depende de HttpClient, que construye esta cadena de interceptores.
  private readonly injector = inject(Injector);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // No agregar token a las rutas de auth
    if (this.isAuthRequest(req)) {
      return next.handle(req);
    }

    const token = this.tokenService.getAccessToken();

    // Si no hay token, simplemente continuar sin él (probablemente sea contenido público)
    if (!token) {
      return next.handle(req);
    }

    // Modo de la pestaña al ENVIAR: el 401 de un token de soporte nunca se
    // trata como el de la sesión normal (ni al revés).
    const supportMode = this.authStorage.isSupportMode();

    // Agregar token a la petición
    req = this.addTokenToRequest(req, token);

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // 403 COMPANY_TERMINATED (término de suscripción): la empresa ya no tiene servicio para
        // esta persona. Vale para cualquier request con sesión, también POST /auth/context y en
        // modo soporte: página «El servicio terminó» y cierre de la sesión.
        const terminated = companyTerminatedDetails(error);
        if (terminated) return this.endCompanyService(terminated);
        // Contraseña incorrecta al firmar o al cambiar credenciales: la sesión sigue viva.
        if (isCredentialCheckFailure(error)) return throwError(() => error);
        if (error.status === 401) {
          return supportMode ? this.endSupportSession() : this.handle401Error(req, next);
        }
        // 402 ACCOUNT_SUSPENDED (suscripción impaga): llevar al paywall.
        // Las rutas de billing quedan fuera para no interferir con el pago.
        if (error.status === 402 && !this.isBillingRequest(req)) {
          this.router.navigate(['/app/billing/suspended']);
        }
        return throwError(() => error);
      }),
    );
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private isBillingRequest(request: HttpRequest<any>): boolean {
    return (
      request.url.includes('/invoices') ||
      request.url.includes('/payments') ||
      request.url.includes('/subscription')
    );
  }

  private isAuthRequest(request: HttpRequest<any>): boolean {
    return (
      request.url.includes('/auth/login') ||
      request.url.includes('/auth/refresh') ||
      request.url.includes('/auth/logout') ||
      request.url.includes('/auth/inspector/request-key') ||
      request.url.includes('/auth/inspector/enter') ||
      // Recuperación/activación de contraseña (H08): se llaman sin sesión —
      // un access token viejo/inválido en storage no debe viajar en ellas.
      // /auth/reset-password/verify queda cubierto también por el check de
      // /auth/reset-password (prefijo), pero se deja explícito por claridad.
      request.url.includes('/auth/forgot-password') ||
      request.url.includes('/auth/reset-password/verify') ||
      request.url.includes('/auth/reset-password') ||
      // Endpoints anónimos del API (link de pago de un cobro, autorizado por
      // la llave `k`): nunca llevan el token ni disparan refresh/logout.
      request.url.startsWith(`${environment.apiUrl}public/`) ||
      // Datos de referencia (regiones y comunas, reference.spec.md): públicos
      // y sin datos personales; el alta los pide sin sesión. Un token viejo en
      // storage no debe viajar ni su 401 cerrar una sesión.
      request.url.startsWith(`${environment.apiUrl}reference/`) ||
      // Canje del código de soporte («Ver como»): público. Nunca lleva el
      // token de la sesión normal de otras pestañas, y su 401 (código
      // inválido, usado o vencido) no debe disparar el refresh/logout de esa
      // sesión.
      request.url.includes('/support/exchange')
    );
  }

  /**
   * «Ver como»: el token de soporte no se renueva ni se revoca con
   * /auth/logout (no hay refresh token y el API prohíbe /auth en soporte). Un
   * 401 significa que la sesión terminó (Terminar, backoffice, vencimiento o
   * permiso revocado): se sale del modo soporte y se va a /support/ended.
   */
  private endSupportSession(): Observable<never> {
    // setTimeout: mismo motivo que en logout() (router dentro del interceptor).
    setTimeout(() => this.injector.get(SupportSessionService).finish('ended'), 0);
    return EMPTY;
  }

  /**
   * Término de suscripción: CompanyTerminationService navega a la página pública y cierra la
   * sesión (en modo soporte sólo sale del modo soporte). El error no llega al componente: la
   * pantalla se va y un aviso de error encima sólo confundiría.
   */
  private endCompanyService(details: CompanyTerminationDetails): Observable<never> {
    // setTimeout: mismo motivo que en logout() (router dentro del interceptor).
    setTimeout(() => this.injector.get(CompanyTerminationService).end(details), 0);
    return EMPTY;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.tokenService.hasValidRefreshToken()) {
      this.logout();
      return EMPTY;
    }

    return this.authService.refreshTokens().pipe(
      map((response) => response.data.tokens.accessToken),
      switchMap((accessToken) => next.handle(this.addTokenToRequest(request, accessToken))),
      catchError((error: HttpErrorResponse) => {
        // The refresh never answers COMPANY_TERMINATED by contract, but the retried request can.
        const terminated = companyTerminatedDetails(error);
        if (terminated) return this.endCompanyService(terminated);
        // The access token had expired AND the password was wrong: the refresh worked, so the
        // retried request's 401 is about the password, not the session.
        if (isCredentialCheckFailure(error)) return throwError(() => error);
        // 429/5xx are transient and must never destroy a still-valid session.
        // Only an invalid/revoked refresh credential ends the session.
        if (error.status === 400 || error.status === 401) this.logout();
        return throwError(() => error);
      }),
    );
  }

  private logout(): void {
    this.authService.logout();

    // Usar setTimeout para evitar problemas con el router en el contexto del interceptor
    setTimeout(() => {
      this.router.navigate(['/login'], {
        queryParams: { reason: 'session_expired' },
      });
    }, 0);
  }
}
